import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Avatar
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TextToSpeech from '../TextToSpeech';
import api from '../../services/api';
import { useClientUiSettings } from '../../hooks/useClientUiSettings';
import { isIOS } from '../../utils/deviceUtils';

/**
 * Helper function to format timestamp
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted timestamp string
 */
const formatMessageTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const year = date.getFullYear();
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${day}/${month}/${year}, ${time}`;
  }
};

/**
 * MessageList Component
 * Renders the chat message list with proper styling and functionality
 *
 * @param {Object} props - Component props
 * @param {Array} props.messages - Array of message objects
 * @param {boolean} props.initialLoading - Whether initial loading is in progress
 * @param {boolean} props.isLoading - Whether a message is currently being processed
 * @param {string} props.chatPhase - Current chat phase ('onboarding' or 'main')
 * @param {string} props.conversationMode - Current conversation mode ('ai', 'human', 'assisted')
 * @param {Function} props.getVoiceIdForMessage - Function to get voice ID for TTS
 * @param {Object} props.messagesEndRef - Ref for scrolling to bottom
 * @returns {JSX.Element} MessageList component
 */
const MessageList = ({
  messages = [],
  initialLoading = false,
  isLoading = false,
  chatPhase = 'main',
  conversationMode = 'ai',
  getVoiceIdForMessage,
  messagesEndRef,
  autoplayEnabled = false,
  currentlyPlayingMessageId = null,
  onPlaybackComplete,
  platformSettings = null,
  therapistSettings = null,
  userSettings = null,
  clientId = null,
  setPlayingAudio = null,
  token = null,
  processingStatus = null,
  onLaunchAssessment = null,
  onGoalProposal = null,
  onGoalDecline = null,
  aiAvatarUrl = null
}) => {
  // Resolved client UI settings (Meeting 3): role label + per-bubble label visibility / style + avatar mode.
  const {
    roleLabel = 'AI Therapist',
    showPerBubbleAILabel = true,
    perBubbleAILabelStyle = 'full',
    aiAvatarDisplay = 'header'
  } = useClientUiSettings();

  // Start with null to indicate "not yet determined"
  const [streamingSupported, setStreamingSupported] = useState(null);
  const [providerDetected, setProviderDetected] = useState(false);

  // Detect provider for client_chat interface
  useEffect(() => {
    const detectProvider = async () => {
      if (!token) {
        console.log('MessageList: No token, skipping provider detection');
        setProviderDetected(true);
        return;
      }

      try {
        console.log('MessageList: Checking provider configuration...');
        // Check provider configuration using the same approach as TextToSpeech component
        const response = await api.get('/tts/provider-info', token, {
          params: { interface: 'client_chat' }
        });
        
        console.log('MessageList: Provider API response:', response);
        const provider = response?.data?.provider?.toLowerCase() || 'elevenlabs';
        console.log('MessageList: Detected provider for client_chat:', provider);
        
        // Only ElevenLabs supports WebSocket streaming — AND only on platforms
        // that can actually play it. iOS (iPhone/iPad Safari, and Chrome on iOS,
        // which is WebKit underneath) doesn't reliably support the streamed
        // MediaSource/WebSocket audio path and throws a "Streaming error: The
        // request is not allowed by the user agent…" (NotAllowedError) on
        // autoplay. Route iOS to the REST path (full audio blob → <audio>),
        // which is far more compatible.
        const supportsStreaming = provider === 'elevenlabs' && !isIOS();
        setStreamingSupported(supportsStreaming);
        console.log('MessageList: Setting streamingSupported to:', supportsStreaming, 'for provider:', provider, 'isIOS:', isIOS());
      } catch (error) {
        console.error('MessageList: Failed to detect provider:', error);
        // Default to false for safety
        setStreamingSupported(false);
      } finally {
        setProviderDetected(true);
      }
    };

    detectProvider();
  }, [token]);

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress data-testid="loading" />
      </Box>
    );
  }

  // Wait for provider detection before rendering messages with TTS
  if (!providerDetected) {
    console.log('MessageList: Waiting for provider detection...');
  }

  // An inline "Start Assessment" button stays in the transcript forever, so an
  // offer the conversation has already moved past remains clickable. In the
  // 13 Aug 2026 SUD round this administered questionnaires clients had just
  // objected to: the AI offered CRAFFT, the client said it was not appropriate
  // for their age, the AI apologised and set it aside — and the button from the
  // earlier message was still live. Four of eight sessions ended up holding a
  // completed, high-risk-scored CRAFFT the client had declined.
  //
  // An offer is current only while it is the AI's most recent word. Once the AI
  // has spoken again the offer has been superseded, so the button is withdrawn.
  // The server-side duplicate guard does not help here: these were FIRST
  // completions, not repeats.
  const lastAiMessageIndex = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m && (m.senderRole === 'ai' || m.senderRole === 'therapist') && !m.isInternal) return i;
    }
    return -1;
  })();

  const isCurrentAssessmentOffer = (index) => index === lastAiMessageIndex;

  // The latest goal proposal that has not been answered. A proposal is answered
  // once a later system line records the goal being created or declined, or a
  // newer proposal supersedes it (ST-20260909-0002).
  let lastGoalOfferIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.metadata?.pendingGoal && m.senderRole === 'ai' && !m.isInternal) { lastGoalOfferIndex = i; break; }
  }
  const goalOfferAnswered = lastGoalOfferIndex >= 0 && messages.slice(lastGoalOfferIndex + 1).some(m =>
    m?.senderRole === 'system' && /^(Goal created|Okay, goal not added|Goal added)/i.test(m.text || '')
  );
  const isCurrentGoalOffer = (index) => index === lastGoalOfferIndex && !goalOfferAnswered;

  console.log('MessageList render - autoplayEnabled:', autoplayEnabled, 'currentlyPlayingMessageId:', currentlyPlayingMessageId, 'streamingSupported:', streamingSupported);
  
  return (
    <>
      <List data-testid="message-list">
        {messages.map((msg, index) => {
          // Skip internal messages (these are for AI context only, not for display)
          // Check both isInternal flag AND message text prefix for backwards compatibility
          // Gen2 Safety: No gen2 reasoning data is exposed here — internal messages
          // (which carry gen2Trace metadata) are filtered out by the isInternal check below.
          // The client MessageList never fetches or displays reasoning traces.
          if (msg.isInternal || (msg.text && msg.text.startsWith('[INTERNAL'))) {
            return null;
          }
          // Video-call transcript lines belong to the therapist's record, never the
          // client's chat; the client sees only the client-facing summary (29 Sep 2026).
          if (msg.sentInMode === 'live_call') {
            return null;
          }

          const isClient = msg.senderRole === 'client';
          const isAI = msg.senderRole === 'ai';
          const isTherapist = msg.senderRole === 'human_therapist';
          const isSystem = msg.senderRole === 'system';
          // Use message-level isOnboarding flag if present, otherwise infer from chatPhase
          const isOnboarding = msg.isOnboarding !== undefined
            ? (msg.isOnboarding && msg.senderRole === 'ai')
            : (chatPhase === 'onboarding' && msg.senderRole === 'ai');

          // Per-message avatar: render based on aiAvatarDisplay mode.
          //   'every_message' → show on every AI bubble.
          //   'grouped'       → show only on the first message of an AI run.
          //   'none' / 'header' → don't show inline.
          let showInlineAiAvatar = false;
          let reserveAvatarSpace = false;
          if (isAI && aiAvatarUrl && (aiAvatarDisplay === 'every_message' || aiAvatarDisplay === 'grouped')) {
            if (aiAvatarDisplay === 'every_message') {
              showInlineAiAvatar = true;
            } else {
              // grouped: walk back to the most recent non-internal message
              let prevAi = false;
              for (let j = index - 1; j >= 0; j--) {
                const prev = messages[j];
                if (!prev || prev.isInternal || (prev.text && prev.text.startsWith('[INTERNAL'))) continue;
                prevAi = prev.senderRole === 'ai';
                break;
              }
              showInlineAiAvatar = !prevAi;
              reserveAvatarSpace = prevAi;
            }
          }

          const borderRadius = isClient ? '15px 15px 0 15px' : '15px 15px 15px 0';
          const bgcolor = isClient
            ? 'primary.main'
            : (isTherapist
              ? 'secondary.light'
              : (isOnboarding ? '#e3f2fd' : 'grey.200')
            );
          const color = isClient ? 'primary.contrastText' : 'text.primary';

          return (
            <ListItem
              key={msg._id || `${index}-${msg.timestamp}`}
              data-testid="message"
              data-sender={msg.senderRole}
              data-message-id={msg._id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isClient 
                  ? 'flex-end' 
                  : (isSystem ? 'center' : 'flex-start'),
                px: 1,
                py: 0.5
              }}
            >
              {isSystem ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    width: '100%'
                  }}
                >
                  {msg.text}
                  {msg.timestamp && (
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: '0.65rem',
                        mt: 0.25
                      }}
                    >
                      {formatMessageTimestamp(msg.timestamp)}
                    </Box>
                  )}
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    flexDirection: isClient ? 'row-reverse' : 'row',
                    gap: 1,
                    // ST-20261006-0001: the avatar and the play button share this row, so
                    // 75% left the text about half the width of a phone.
                    maxWidth: { xs: '94%', sm: '75%' }
                  }}
                >
                  {showInlineAiAvatar && (
                    <Avatar
                      src={aiAvatarUrl}
                      alt="AI"
                      sx={{ width: 28, height: 28, alignSelf: 'flex-end', mb: 0.25 }}
                    />
                  )}
                  {reserveAvatarSpace && (
                    // Keep grouped messages aligned with the avatar above.
                    <Box sx={{ width: 28, flexShrink: 0 }} aria-hidden="true" />
                  )}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      borderRadius,
                      bgcolor,
                      color,
                      wordWrap: 'break-word',
                      minWidth: 0,
                      // 'subtle' style positions the AI label as a small corner tag
                      position: 'relative'
                    }}
                  >
                    {isTherapist && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          mb: 0.5,
                          fontWeight: 'bold',
                          color: 'secondary.dark'
                        }}
                      >
                        Therapist
                      </Typography>
                    )}
                    {isOnboarding && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          mb: 0.5,
                          fontWeight: 'bold',
                          color: '#1976d2'
                        }}
                      >
                        Onboarding Assistant
                      </Typography>
                    )}
                    {isAI && !isOnboarding && showPerBubbleAILabel && (
                      perBubbleAILabelStyle === 'subtle' ? (
                        <Typography
                          variant="caption"
                          aria-label={`Message from ${roleLabel}`}
                          sx={{
                            position: 'absolute',
                            top: -14,
                            left: 4,
                            fontSize: '0.62rem',
                            opacity: 0.65,
                            fontWeight: 500,
                            color: 'text.secondary',
                            pointerEvents: 'none',
                            lineHeight: 1,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {roleLabel}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{
                            mb: 0.5,
                            fontWeight: 'bold',
                            color: 'primary.main'
                          }}
                        >
                          {roleLabel}
                        </Typography>
                      )
                    )}
                    <Typography variant="body1">
                      {msg.text}
                    </Typography>
                    {msg.metadata?.pendingGoal && onGoalProposal && isCurrentGoalOffer(index) && (
                      <Box
                        sx={{
                          mt: 1.5, p: 1.5, borderRadius: '12px',
                          border: '1px solid', borderColor: 'primary.light', bgcolor: 'background.paper'
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Suggested goal
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {msg.metadata.pendingGoal.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => onGoalProposal({
                              description: msg.metadata.pendingGoal.description,
                              goalType: msg.metadata.pendingGoal.goalType || 'one_off',
                              originalAiText: msg.text,
                              originalFunctionCall: msg.metadata.pendingGoal.originalFunctionCall || null
                            })}
                            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 500, fontSize: '0.85rem' }}
                          >
                            Set this goal
                          </Button>
                          {onGoalDecline && (
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => onGoalDecline()}
                              sx={{ borderRadius: '20px', textTransform: 'none', fontSize: '0.85rem' }}
                            >
                              Not now
                            </Button>
                          )}
                        </Box>
                      </Box>
                    )}
                    {msg.metadata?.pendingAssessment && onLaunchAssessment && isCurrentAssessmentOffer(index) && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AssignmentIcon />}
                        onClick={() => onLaunchAssessment(
                          msg.metadata.pendingAssessment.testKey,
                          msg.metadata.pendingAssessment.testName,
                          msg.metadata.pendingAssessment.testDescription,
                          'ai'
                        )}
                        sx={{
                          mt: 1.5,
                          borderRadius: '20px',
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.85rem',
                          bgcolor: 'primary.main',
                          '&:hover': { bgcolor: 'primary.dark' }
                        }}
                      >
                        Start {msg.metadata.pendingAssessment.testName || 'Assessment'}
                      </Button>
                    )}
                  </Paper>
                  
                  {(isAI || isTherapist) && (() => {
                    const shouldAutoplay = autoplayEnabled && (msg._id === currentlyPlayingMessageId || (!msg._id && String(msg.timestamp) === currentlyPlayingMessageId));
                    if (shouldAutoplay) {
                      console.log('MessageList: Autoplay enabled for message:', msg._id || msg.timestamp, 'currentlyPlayingMessageId:', currentlyPlayingMessageId);
                      console.log('MessageList: Using streamingSupported:', streamingSupported, 'providerDetected:', providerDetected);
                    }
                    return (
                      <Box sx={{ [isClient ? 'mr' : 'ml']: 1 }}>
                        <TextToSpeech
                          text={msg.text}
                          size="small"
                          disabled={isLoading || initialLoading || !providerDetected}
                          useStreaming={streamingSupported === true}
                          autoplay={shouldAutoplay && providerDetected && streamingSupported !== null}
                          onPlaybackComplete={onPlaybackComplete}
                          platformSettings={platformSettings}
                          therapistSettings={therapistSettings}
                          userSettings={userSettings}
                          clientId={clientId}
                          token={token}
                          useBackendTTS={true}
                          senderRole={msg.senderRole}
                          setPlayingAudio={setPlayingAudio}
                          voiceId={getVoiceIdForMessage ? getVoiceIdForMessage(msg) : null}
                        />
                      </Box>
                    );
                  })()}
                </Box>
              )}
              
              {!isSystem && msg.timestamp && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    alignSelf: isClient ? 'flex-end' : 'flex-start',
                    mr: isClient ? (isAI || isTherapist ? 4 : 0.5) : 0,
                    ml: !isClient ? (isAI || isTherapist ? 4 : 0.5) : 0
                  }}
                >
                  {formatMessageTimestamp(msg.timestamp)}
                </Typography>
              )}
            </ListItem>
          );
        })}
      </List>
      
      {/* Only show typing indicator in AI mode - human/assisted modes don't show it */}
      {isLoading && !initialLoading && conversationMode === 'ai' && (
        <ListItem
          data-testid="loading"
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            px: 1,
            py: 0.5
          }}
        >
          <Paper
            elevation={1}
            sx={{
              p: 1.5,
              borderRadius: '15px 15px 15px 0',
              bgcolor: 'grey.200',
              color: 'text.secondary',
              fontStyle: 'italic'
            }}
          >
            <Typography variant="body1">{processingStatus || 'AI is typing...'}</Typography>
          </Paper>
        </ListItem>
      )}
      
      <div ref={messagesEndRef} />
    </>
  );
};

export default MessageList;